import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector, useUser } from '../../../app/hooks';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { TpTemplateInfo } from '../../../api/models/assets';
import { selectDashboardTemplateForAsset, selectDashboardTemplateById } from '../../../data/templateSlice';
import { TEMPLATE_QUERY_PARAM } from '../../../data/templateSlice.helpers';

interface UseActiveDashboardTemplateOptions {
  assetInfo: TpAssetInfo;
  isAssetTemplate?: boolean;
}

export function useActiveDashboardTemplate({
  assetInfo,
  isAssetTemplate,
}: UseActiveDashboardTemplateOptions): TpTemplateInfo | undefined {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const urlTemplateId = searchParams.get(TEMPLATE_QUERY_PARAM);

  // 1. Unconditional selector for the fallback template
  const fallbackTemplate = useAppSelector((state) =>
    selectDashboardTemplateForAsset(state, assetInfo.type, isAssetTemplate ? assetInfo.id : undefined),
  );

  // 2. Unconditional selector for the URL-targeted template
  const rawUrlTemplate = useAppSelector((state) =>
    urlTemplateId ? selectDashboardTemplateById(state, assetInfo.type, urlTemplateId) : undefined,
  );

  // 3. Permission check: non-admins cannot load inactive templates
  const isAllowed = rawUrlTemplate ? rawUrlTemplate.isActive || user.isOrganizationAdmin : false;
  const validUrlTemplate = isAllowed ? rawUrlTemplate : undefined;

  // 4. Side effect: strip invalid or unauthorized ?template=... param from the URL
  useEffect(() => {
    if (urlTemplateId && !validUrlTemplate) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(TEMPLATE_QUERY_PARAM);
          return next;
        },
        { replace: true },
      );
    }
  }, [urlTemplateId, validUrlTemplate, setSearchParams]);

  return validUrlTemplate ?? fallbackTemplate;
}
